import React, { useCallback, useRef, type PropsWithChildren } from 'react';
import { StartWithTemplateSpotlight } from '@jira/polaris__lib-onboarding-flows/src/StartWithTemplateSpotlight.tsx';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ModalEntryPointTrigger } from './ModalEntryPointTrigger.tsx';

type StartWithTemplateSpotlightWithModalProps = PropsWithChildren<{
	projectId: string;
	projectKey: string;
	containerId?: string;
	spotlightTargetNode?: HTMLElement | null;
	isDelayEligible?: boolean;
}>;

const hiddenModalTriggerLabel = defineMessage({
	id: 'component-view-template-library.start-with-template-spotlight-with-modal.hidden-modal-trigger-label',
	defaultMessage: 'Open template library',
	description:
		'Hidden trigger button label used to open the view template library modal from spotlight.',
});

export const StartWithTemplateSpotlightWithModal = ({
	children,
	projectId,
	projectKey,
	containerId,
	spotlightTargetNode,
	isDelayEligible,
}: StartWithTemplateSpotlightWithModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const triggerRef = useRef<HTMLButtonElement>(null);

	const handleTryItNow = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'button clicked',
			'openViewTemplateLibrary',
		);

		triggerRef.current?.click();
	}, [createAnalyticsEvent]);

	return (
		<>
			<ModalEntryPointTrigger
				projectId={projectId}
				projectKey={projectKey}
				containerId={containerId}
			>
				{({ ref }) => (
					<Pressable hidden ref={mergeRefs(triggerRef, ref)}>
						{formatMessage(hiddenModalTriggerLabel)}
					</Pressable>
				)}
			</ModalEntryPointTrigger>
			<StartWithTemplateSpotlight
				onTryItNow={handleTryItNow}
				spotlightTargetNode={spotlightTargetNode}
				isDelayEligible={isDelayEligible}
			>
				{children}
			</StartWithTemplateSpotlight>
		</>
	);
};
